import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { consumerApi } from 'lib/consumer-api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { Receipt, Search } from 'lucide-react';
import ConsumerListPageLayout from 'components/consumer/ConsumerListPageLayout';
import { ConsumerFilterField, consumerFilterInputClass, consumerFilterInputMonoClass } from 'components/consumer/ConsumerFilterBar';
import {
  CONSUMER_TD_CLASS,
  CONSUMER_TH_CLASS,
  CONSUMER_CELL_TEXT,
  CONSUMER_CELL_HEADING,
  CONSUMER_CELL_MUTED,
  CONSUMER_CELL_LINK,
} from 'components/consumer/consumer-list-styles';
import ConsumerUuidCell from 'components/consumer/ConsumerUuidCell';
import { appendCursorParams, parseCursorPagination } from 'lib/cursor-pagination';
import { useCursorListPage } from 'hooks/useCursorListPage';
import { hasPlayerFilter, playerFiltersFromUrl } from 'lib/player-url-filters';
import { LEDGER_TYPE, LEDGER_TYPE_LABEL, transactionTypeLabel } from 'constants/enums';
import type { ConsumerPortalTransaction } from 'types/consumer';

type TransactionFilters = { txnId: string; username: string; type: string; userId: string };

const emptyFilters: TransactionFilters = { txnId: '', username: '', type: '', userId: '' };

const TYPE_OPTIONS: [string, string][] = Object.entries(LEDGER_TYPE_LABEL)
  .filter(([code]) => Number(code) !== LEDGER_TYPE.ADMIN_ADJUSTMENT)
  .map(([code, label]) => [code, label] as [string, string]);

const countFilters = (f: TransactionFilters) =>
  (f.txnId.trim() ? 1 : 0) +
  (f.type ? 1 : 0) +
  (f.username.trim() ? 1 : 0);

function buildFilterParams(filters: TransactionFilters) {
  const params = new URLSearchParams();
  if (filters.userId.trim()) {
    params.set('userId', filters.userId.trim());
  }
  if (filters.username.trim()) {
    params.set('username', filters.username.trim());
  }
  if (filters.txnId.trim()) params.set('txnId', filters.txnId.trim());
  if (filters.type) params.set('type', filters.type);
  return params;
}

const ConsumerTransactions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [draftFilters, setDraftFilters] = useState<TransactionFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<TransactionFilters>(emptyFilters);
  const [playerFilterLabel, setPlayerFilterLabel] = useState('');

  const fetchPage = useCallback(async (cursor: number | null) => {
    const params = appendCursorParams(buildFilterParams(appliedFilters), cursor);
    const res = await consumerApi.get<ConsumerPortalTransaction[]>(`${API_ENDPOINTS.CONSUMER_PORTAL.TRANSACTIONS}?${params.toString()}`);
    const meta = parseCursorPagination(res);
    return { items: res.success && Array.isArray(res.data) ? res.data : [], nextCursor: meta.nextCursor, hasMore: meta.hasMore, total: meta.total, totalPages: meta.totalPages };
  }, [appliedFilters]);

  const { page, items, loading, refreshing, total, totalPages, limit, resetAndLoad, refreshCurrent, goFirst, goToPage, goLast } = useCursorListPage(fetchPage);

  useEffect(() => {
    if (location.pathname !== '/consumer/transactions') return;
    const playerFilters = playerFiltersFromUrl(location.search);
    if (playerFilters) {
      const next: TransactionFilters = {
        ...emptyFilters,
        userId: playerFilters.userId,
        username: playerFilters.username,
      };
      setDraftFilters(next);
      setAppliedFilters(next);
      setPlayerFilterLabel(playerFilters.username);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  useEffect(() => {
    resetAndLoad();
  }, [appliedFilters, resetAndLoad]);

  const clearAllFilters = () => {
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPlayerFilterLabel('');
    navigate('/consumer/transactions', { replace: true });
  };

  const clearPlayerFilter = () => {
    const next = { ...appliedFilters, userId: '', username: '' };
    setDraftFilters((prev) => ({ ...prev, userId: '', username: '' }));
    setAppliedFilters(next);
    setPlayerFilterLabel('');
    navigate('/consumer/transactions', { replace: true });
  };

  const openUser = (userId: number) => navigate(`/consumer/users/${userId}`);

  return (
    <ConsumerListPageLayout
      icon={Receipt} title="Transactions" description="Recent transactions for your consumer (read-only)."
      totalCount={null} filteredCount={items.length}
      loading={loading} refreshing={refreshing} onRefresh={() => refreshCurrent()}
      activeFilterCount={countFilters(appliedFilters)} draftFilterCount={countFilters(draftFilters)}
      onApplyFilters={() => setAppliedFilters({ ...draftFilters, userId: '' })}
      onClearFilters={clearAllFilters}
      playerFilterLabel={hasPlayerFilter(appliedFilters) ? playerFilterLabel || appliedFilters.username || `User #${appliedFilters.userId}` : undefined}
      onClearPlayerFilter={hasPlayerFilter(appliedFilters) ? clearPlayerFilter : undefined}
      filterFields={
        <>
          <ConsumerFilterField label="Txn ID">
            <input
              type="text"
              placeholder="Partial ID"
              value={draftFilters.txnId}
              onChange={(e) => setDraftFilters((p) => ({ ...p, txnId: e.target.value }))}
              className={consumerFilterInputMonoClass}
            />
          </ConsumerFilterField>
          <ConsumerFilterField label="Username">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={15} />
              <input
                type="text"
                placeholder="Search username"
                value={draftFilters.username}
                onChange={(e) => setDraftFilters((p) => ({ ...p, username: e.target.value, userId: '' }))}
                className={`${consumerFilterInputClass} pl-9`}
              />
            </div>
          </ConsumerFilterField>
          <ConsumerFilterField label="Type">
            <select
              value={draftFilters.type}
              onChange={(e) => setDraftFilters((p) => ({ ...p, type: e.target.value }))}
              className={consumerFilterInputClass}
            >
              <option value="">All types</option>
              {TYPE_OPTIONS.map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </ConsumerFilterField>
        </>
      }
      tableTitle="Transaction Ledger" tableSubtitle="Wallet movements for your consumer players"
      emptyIcon={Receipt} emptyTitle="No transactions yet" emptyDescription="Transactions will appear here once players play games."
      isDatasetEmpty={total === 0 && !loading && countFilters(appliedFilters) === 0}
      isFilteredEmpty={items.length === 0 && !loading && countFilters(appliedFilters) > 0}
      colSpan={5}
      page={page}
      itemCount={items.length}
      limit={limit}
      total={total}
      totalPages={totalPages}
      onFirst={goFirst}
      onPageChange={goToPage}
      onLast={goLast}
      tableHead={
        <tr>
          <th className={CONSUMER_TH_CLASS}>Txn ID</th>
          <th className={CONSUMER_TH_CLASS}>User</th>
          <th className={CONSUMER_TH_CLASS}>Amount</th>
          <th className={CONSUMER_TH_CLASS}>Type</th>
          <th className={CONSUMER_TH_CLASS}>Created</th>
        </tr>
      }
      tableBody={
        <>
          {items.map((t) => (
            <tr key={t.id} className="hover:bg-subtle/40 transition-colors">
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <ConsumerUuidCell
                  value={t.transactionUuid}
                  fallback={t.id}
                  label="transaction ID"
                />
              </td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                {t.userId ? (
                  <button type="button" onClick={() => openUser(t.userId)} className={CONSUMER_CELL_LINK}>
                    {t.username || `User #${t.userId}`}
                  </button>
                ) : (
                  <span className={CONSUMER_CELL_TEXT}>{t.username || '—'}</span>
                )}
              </td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <span className={CONSUMER_CELL_HEADING}>₹{Number(t.amount).toLocaleString()}</span>
              </td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <span className={CONSUMER_CELL_TEXT}>{transactionTypeLabel(t.transactionType)}</span>
              </td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <span className={CONSUMER_CELL_MUTED}>
                  {t.createdAt ? new Date(t.createdAt).toLocaleString() : '—'}
                </span>
              </td>
            </tr>
          ))}
        </>
      }
    />
  );
};

export default ConsumerTransactions;
